// Admin "Manage as user" (impersonation), start and end.
//
// Both go through Spring Security's SwitchUserFilter (configured in SecurityConfig) as native form
// posts / navigations rather than fetch+JSON, for two reasons:
//
//   1. The filter is what grants the session its SwitchUserGrantedAuthority. That authority is both
//      the impersonation signal MeResponse.impersonating reads AND what /endmanagingsession later
//      uses to restore the original admin. A controller doing a manual SecurityContext swap does
//      not persist that in Spring Security 6, so it silently fails to impersonate.
//   2. Switching replaces the whole session identity, so a full-page navigation is wanted — every
//      store and cached response in the SPA belongs to the previous identity. The filter's success
//      handler redirects to /account (start) or / (end), so the browser re-bootstraps from scratch.
//
// Hence: build a real <form> and submit it, not an XHR.

/**
 * The CSRF token the SPA already holds. Both security chains use CookieCsrfTokenRepository, so the
 * XSRF-TOKEN cookie set during the normal /api/v1 bootstrap is also valid for these form posts.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function postForm(action: string, fields: Record<string, string> = {}): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  // Spring reads the form parameter `_csrf` for non-AJAX posts. Omitted only where the server
  // explicitly exempts the path (/endmanagingsession), where a missing token is harmless.
  const token = csrfToken()
  const all = token ? { ...fields, _csrf: token } : fields
  for (const [name, value] of Object.entries(all)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

/**
 * Start managing another account. `username` is the SwitchUserFilter's parameter name; wwboot's
 * UserDetailsService resolves a numeric userID, which is what the admin list has on hand.
 *
 * The POST carries the SPA's CSRF token: /manage is NOT exempt from CSRF, because it changes the
 * session's identity. The endpoint is additionally ADMIN-gated in the filter chain.
 */
export function manageAsUser(userID: number): void {
  postForm('/manage', { username: String(userID) })
}

/** End a managing session and return to the original admin identity. */
export function endManagingSession(): void {
  postForm('/endmanagingsession')
}
